import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    AppBar, Toolbar, Typography, Box, Container, Button, IconButton, Tabs, Tab, Paper,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
    FormControl, InputLabel, Select, MenuItem, OutlinedInput, Checkbox, ListItemText, SelectChangeEvent,
    CircularProgress
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuthContext } from '../context/AuthContext';
import { usePersistentNavigate } from '../utils/navigation';
import { UserType, Admin, Provider, Reviewer } from '../schema';
import { User } from '../getusers';
import { Case } from '../getcases';
import { getUsers } from '../queries/getusers';
import { getCases } from '../queries/getcases';

const AdminPortal: React.FC = () => {
    const navigate = usePersistentNavigate();
    const [searchParams] = useSearchParams();
    const { accessToken, logout, userType, setAccessToken } = useAuthContext();
    const [view, setView] = useState<'users' | 'cases'>('users');
    const [userTypeFilter, setUserTypeFilter] = useState<UserType>(UserType.PROVIDER);
    const [users, setUsers] = useState<User[]>([]);
    const [cases, setCases] = useState<Case[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loading, setLoading] = useState(false);

    const fetchUsers = useCallback(async () => {
        if (!accessToken) return;
        setLoading(true);
        try {
            const response = await getUsers({ userType: userTypeFilter, offset: page * rowsPerPage, pageSize: rowsPerPage }, accessToken, setAccessToken, logout);
            setUsers(response.users);
        } catch (error) {
            console.error("Failed to fetch users:", error);
        } finally {
            setLoading(false);
        }
    }, [accessToken, userTypeFilter, page, rowsPerPage, setAccessToken, logout]);

    const fetchCases = useCallback(async () => {
        if (!accessToken) return;
        setLoading(true);
        try {
            const response = await getCases({ offset: page * rowsPerPage, pageSize: rowsPerPage }, accessToken, setAccessToken, logout);
            setCases(response.cases);
        } catch (error) {
            console.error("Failed to fetch cases:", error);
        } finally {
            setLoading(false);
        }
    }, [accessToken, page, rowsPerPage, setAccessToken, logout]);

    useEffect(() => {
        if (userType !== UserType.ADMIN) {
            navigate('/');
        }
    }, [userType, navigate]);

    useEffect(() => {
        if (view === 'users') {
            fetchUsers();
        } else {
            fetchCases();
        }
    }, [view, fetchUsers, fetchCases]);

    const handleViewChange = (event: React.SyntheticEvent, newValue: 'users' | 'cases') => {
        setView(newValue);
        setPage(0);
    };

    const handleUserTypeChange = (event: SelectChangeEvent<number>) => {
        setUserTypeFilter(event.target.value as UserType);
        setPage(0);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const renderTable = () => {
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (view === 'users') {
            const data = users.reduce<(Admin | Provider | Reviewer)[]>((acc, u) => {
                if (u.admin) acc.push(u.admin);
                else if (u.provider) acc.push(u.provider);
                else if (u.reviewer) acc.push(u.reviewer);
                return acc;
            }, []);
            if (data.length === 0) return <Typography sx={{ p: 2 }}>No users found.</Typography>;
            const columns = Object.keys(data[0]);
            return (
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 800 }}>
                        <TableHead>
                            <TableRow>
                                {columns.map(col => <TableCell key={col}>{col}</TableCell>)}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.map((item, index) => (
                                <TableRow key={index}>
                                    {columns.map(col => <TableCell key={col}>{String(item[col as keyof typeof item])}</TableCell>)}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            );
        } else {
            if (cases.length === 0) return <Typography sx={{ p: 2 }}>No cases found.</Typography>;
            const columns = Object.keys(cases[0]);
            return (
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 800 }}>
                        <TableHead>
                            <TableRow>
                                {columns.map(col => <TableCell key={col}>{col}</TableCell>)}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {cases.map((item) => (
                                <TableRow
                                    key={item.id}
                                    onClick={() => {
                                        const newSearch = new URLSearchParams();
                                        newSearch.set('case_id', item.id);
                                        newSearch.set('from', 'admin');
                                        if (searchParams.has('stagingEnv')) {
                                            newSearch.set('stagingEnv', searchParams.get('stagingEnv')!);
                                        }
                                        navigate({
                                            pathname: '/case-report',
                                            search: `?${newSearch.toString()}`
                                        });
                                    }}
                                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                                >
                                    {columns.map(col => <TableCell key={col}>{String(item[col as keyof typeof item])}</TableCell>)}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            );
        }
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <AppBar position="static" color="default" elevation={0} sx={{ backgroundColor: 'white', borderBottom: '1px solid #e6e6e6' }}>
                <Toolbar sx={{ justifyContent: 'space-between' }}>
                    <Typography variant="h5" component="div" sx={{ color: '#2c5282', fontWeight: 'bold' }}>
                        PRECI<span style={{ color: '#4299e1' }}>SCA</span> Admin
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/')}
                            sx={{
                                color: '#2c5282',
                                borderColor: '#2c5282',
                                mr: 2,
                                '&:hover': {
                                    borderColor: '#4299e1',
                                    color: '#4299e1'
                                }
                            }}
                        >
                            Return to Homepage
                        </Button>
                        <Button variant="contained" onClick={handleLogout}>Log Out</Button>
                        <IconButton color="inherit" sx={{ color: '#2c5282' }}><MenuIcon /></IconButton>
                    </Box>
                </Toolbar>
            </AppBar>
            <Container maxWidth={false} disableGutters sx={{ mt: 4, mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 2 }}>
                    <Typography variant="h4" component="h1">Admin Portal</Typography>
                    <Button variant="contained" onClick={() => navigate('/register-admin')}>Register New Admin</Button>
                </Box>
                <Paper sx={{ mx: 2 }}>
                    <Tabs value={view} onChange={handleViewChange} indicatorColor="primary" textColor="primary">
                        <Tab label="Users" value="users" />
                        <Tab label="Cases" value="cases" />
                    </Tabs>
                    {view === 'users' && (
                        <Box sx={{ p: 2 }}>
                            <FormControl size="small">
                                <InputLabel>User Type</InputLabel>
                                <Select value={userTypeFilter} label="User Type" onChange={handleUserTypeChange}>
                                    <MenuItem value={UserType.PROVIDER}>Providers</MenuItem>
                                    <MenuItem value={UserType.REVIEWER}>Reviewers</MenuItem>
                                    <MenuItem value={UserType.ADMIN}>Admins</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    )}
                    {renderTable()}
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={-1} // Unknown count for now
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            </Container>
        </Box>
    );
};

export default AdminPortal;